import { ChevronLeftIcon, ChevronRightIcon, KebabHorizontalIcon, TrashIcon } from '@primer/octicons-react';
import { GraphQLError } from 'graphql';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import { graphql } from 'react-relay';

import BookActionsMutationButtonStack from '../../components/book/BookActionsMutationButtonStack';
import BookBookScrapCreateDialog from '../../components/book/BookBookScrapCreateDialog';
import BookBookScrapDeleteDialog from '../../components/book/BookBookScrapDeleteDialog';
import BookBookScrapPaginator, {
  clearLoadedCount,
  removeBookScrapsFromList,
} from '../../components/book/BookBookScrapPaginator';
import BookBookScrapsClassificationDialog from '../../components/book/BookBookScrapsClassificationDialog';
import BookDescriptionList from '../../components/book/BookDescriptionList';
import BookUpdateDialog from '../../components/book/BookUpdateDialog';
import BookScrapConnectionDataTable from '../../components/bookScrap/BookScrapConnectionDataTable';
import ActionList from '../../components/core/ActionList';
import ActionMenu from '../../components/core/ActionMenu';
import Button from '../../components/core/Button';
import Card from '../../components/core/Card';
import Checkbox from '../../components/core/Checkbox';
import ConfirmButton from '../../components/core/ConfirmButton';
import { RowWrapper } from '../../components/core/DataTable';
import DialogButton from '../../components/core/DialogButton';
import DialogHandler from '../../components/core/DialogHandler';
import EmptyState from '../../components/core/EmptyState';
import Grid from '../../components/core/Grid';
import Head from '../../components/core/Head';
import HorizontalDivider from '../../components/core/HorizontalDivider';
import IconButton from '../../components/core/IconButton';
import { HeaderSidebarNavPageLayout } from '../../components/core/Layout';
import Link from '../../components/core/Link';
import MutationFormik from '../../components/core/MutationFormik';
import Stack from '../../components/core/Stack';
import StyledOcticon from '../../components/core/StyledOcticon';
import Text from '../../components/core/Text';
import View from '../../components/core/View';
import { useHeaderSidebarLayoutContext } from '../../contexts/HeaderSidebarLayoutContext';
import useAlert from '../../hooks/useAlert';
import useInitialValuesFromParsedUrlQuery from '../../hooks/useInitialValuesFromParsedUrlQuery';
import useLazyLoadQuery from '../../hooks/useLazyLoadQuery';
import useToast from '../../hooks/useToast';
import { BookId_bookDeleteMutation } from '../../relay/__generated__/BookId_bookDeleteMutation.graphql';
import { BookFilter, BookId_bookQuery } from '../../relay/__generated__/BookId_bookQuery.graphql';
import { BookId_bookScrapsDeleteMutation } from '../../relay/__generated__/BookId_bookScrapsDeleteMutation.graphql';
import { parseGraphQLError } from '../../utils/error';
import { isNullable } from '../../utils/is';
import { numberWithCommas } from '../../utils/number';
import { parseOrdering } from '../../utils/order';
import { NextPage } from '../_app';

const bookForBookId = graphql`
  query BookId_bookQuery($id: ID!, $filters: BookFilter, $order: BookOrder) {
    book(id: $id) {
      id
      sequence
      status
      actions
      mathkingSyncFailed
      unclassifiedBookScrapCount
      previous(order: $order, filters: $filters)
      next(order: $order, filters: $filters)

      title
      coverPdf {
        key
        objectUrl
      }
      problemPdf {
        key
        objectUrl
      }
      solutionPdf {
        key
        objectUrl
      }
      ...BookUpdateDialog_book
      ...BookBookScrapDeleteDialog_book
      ...BookDescriptionList_book
      ...BookActionsMutationButtonStack_book
      ...BookBookScrapPaginator_book
      ...BookBookScrapCreateDialog_book
      ...BookBookScrapsClassificationDialog_book
    }
  }
`;

const BookId: NextPage = () => {
  const router = useRouter();
  const { toast } = useToast();
  const { alert } = useAlert();
  const { headerHeight } = useHeaderSidebarLayoutContext();
  // 삭제 응답을 받았을 때 목록에서 뺄 문제들
  const deletingBookScrapIdsRef = useRef<readonly string[]>([]);
  // shift 클릭 범위 선택의 시작점
  const lastClickedBookScrapIdRef = useRef<string | null>(null);
  const isShiftClickRef = useRef(false);

  const { initialValues } = useInitialValuesFromParsedUrlQuery({
    search: { type: 'string' },
    order: { type: 'string' },
    status_In: { type: 'string', multiple: true },
    type_In: { type: 'string', multiple: true },
    unitAId_In: { type: 'string', multiple: true },
    publisher_In: { type: 'string', multiple: true },
  });

  const { order, ...filters } = initialValues;

  const [{ book }] = useLazyLoadQuery<BookId_bookQuery>(bookForBookId, {
    id: router.query.bookId as string,
    order: order ? parseOrdering(order as string) : undefined,
    filters: filters as BookFilter,
  });

  // 제출한 내용이 수학대왕에 끝내 반영되지 않은 교재. 상태는 이미 '진행 중' 으로 돌아와 있다.
  const mathkingSyncFailed = book?.mathkingSyncFailed ?? false;
  useEffect(() => {
    if (mathkingSyncFailed) alert('출시에 실패했습니다. 다시 시도해주세요.');
    // 교재 id 도 같이 본다. 이 페이지는 앞/뒤 교재로 옮겨도 그대로 붙어 있어서,
    // 실패한 교재가 연달아 있으면 값이 안 바뀌어 두 번째부터 안 뜬다.
  }, [book?.id, mathkingSyncFailed]);

  // 다른 교재로 옮기거나 나가면 불러 뒀던 수를 잊는다.
  useEffect(() => {
    const bookId = book?.id;
    return () => {
      if (bookId) clearLoadedCount(bookId);
    };
  }, [book?.id]);

  if (!book) return null;
  const {
    id,
    previous: previousBookId,
    next: nextBookId,
    status,
    actions,
    sequence,
    title,
    coverPdf,
    problemPdf,
    solutionPdf,
    unclassifiedBookScrapCount,
  } = book;

  const hasPrevious = !isNullable(previousBookId);
  const handlePreviousButtonClick = () => {
    const pathname = `/book/${previousBookId}`;
    router.replace({ pathname, query: initialValues }, pathname);
  };

  const hasNext = !isNullable(nextBookId);
  const handleNextButtonClick = () => {
    const pathname = `/book/${nextBookId}`;
    router.replace({ pathname, query: initialValues }, pathname);
  };

  return (
    <View>
      <Head siteTitle={`시중 교재 스크랩 상세`} />
      <Grid gapX={2} sx={{ alignItems: 'center' }}>
        <Grid.Unit size={'max'}>
          <Stack gapX={3}>
            <Stack.Item>
              <IconButton
                icon={ChevronLeftIcon}
                aria-label={'Previous Book Id'}
                disabled={!hasPrevious}
                onClick={() => handlePreviousButtonClick()}
              />
            </Stack.Item>
            <Stack.Item>
              <Text as={'h1'}>{sequence}</Text>
            </Stack.Item>
            <Stack.Item>
              <IconButton
                icon={ChevronRightIcon}
                aria-label={'Next Book Id'}
                disabled={!hasNext}
                onClick={() => handleNextButtonClick()}
              />
            </Stack.Item>
          </Stack>
        </Grid.Unit>
        <Grid.Unit size={'min'}>
          <BookActionsMutationButtonStack book={book} />
        </Grid.Unit>
        <Grid.Unit size={'min'}>
          {actions.includes('book_delete') ? (
            <ActionMenu>
              <ActionMenu.Anchor>
                <IconButton icon={KebabHorizontalIcon} variant={'plain'} aria-label="Open column options" />
              </ActionMenu.Anchor>
              <ActionMenu.Overlay>
                <ActionList>
                  <ActionList.MutationItem<BookId_bookDeleteMutation>
                    mutation={graphql`
                      mutation BookId_bookDeleteMutation($input: BookDeleteInput!) {
                        bookDelete(input: $input) {
                          id @deleteRecord
                        }
                      }
                    `}
                    input={{ id }}
                    variant={'danger'}
                    config={{
                      onCompleted: () => {
                        router.replace('/book').then(() => toast('시중 교재 스크랩 삭제 완료!', 'success'));
                      },
                      onError: () => {
                        toast('시중 교재 스크랩 삭제에 실패했어요', 'error');
                      },
                    }}
                    message={'정말 삭제할까요?'}
                  >
                    <ActionList.LeadingVisual>
                      <TrashIcon />
                    </ActionList.LeadingVisual>
                    삭제하기
                  </ActionList.MutationItem>
                </ActionList>
              </ActionMenu.Overlay>
            </ActionMenu>
          ) : null}
        </Grid.Unit>
      </Grid>
      <HorizontalDivider mt={[3, 3, 0]} mb={5} />
      <Grid gapX={5} gapY={3}>
        <Grid.Unit size={[1, 1, 3 / 4]}>
          <Grid gapX={1} sx={{ alignItems: 'center' }}>
            <Grid.Unit size={'max'}>
              <Text sx={{ fontSize: 1, fontWeight: 'bold', color: 'fg.default' }}>{title}</Text>
            </Grid.Unit>
            {actions.includes('book_update') ? (
              <Grid.Unit size={'min'}>
                <DialogButton
                  renderDialog={({ closeDialog, isOpen }) => (
                    <BookUpdateDialog
                      book={book}
                      isOpen={isOpen}
                      onDismiss={closeDialog}
                      config={{
                        onCompleted: () => {
                          toast('PDF 정보가 수정됐어요', 'success');
                          closeDialog();
                        },
                      }}
                    />
                  )}
                  size={'large'}
                  variant={'outline'}
                >
                  수정하기
                </DialogButton>
              </Grid.Unit>
            ) : null}
          </Grid>
          <Card sx={{ marginTop: 2, padding: 3 }}>
            <BookDescriptionList
              book={book}
              titleUnitSize={[1, 1, 1 / 6]}
              descriptionUnitSize={[1, 1, 5 / 6]}
              gapX={6}
              renderTitle={(title) => (
                <Text fontWeight={'normal'} fontSize={1} color={'fg.subtle'}>
                  {title}
                </Text>
              )}
            />
          </Card>
          <View sx={{ marginTop: 5 }}>
            <Text sx={{ fontSize: 1, fontWeight: 'bold' }}>문제 표지 PDF</Text>
          </View>
          <Card sx={{ marginTop: 2 }}>
            <Link href={coverPdf.objectUrl} target={'_blank'}>
              <Grid gapX={1} wrap={false} sx={{ padding: 3 }}>
                <Grid.Unit size={'max'}>
                  <Text sx={{ fontSize: 1 }}>{coverPdf.key}</Text>
                </Grid.Unit>
                <Grid.Unit size={'min'}>
                  <Text sx={{ fontSize: 1 }} color={'fg.subtle'}>
                    PDF 열기
                  </Text>
                </Grid.Unit>
                <Grid.Unit size={'min'}>
                  <StyledOcticon icon={ChevronRightIcon} color={'fg.subtle'} />
                </Grid.Unit>
              </Grid>
            </Link>
          </Card>
          <View sx={{ marginTop: 5 }}>
            <Text sx={{ fontSize: 1, fontWeight: 'bold' }}>문제 PDF</Text>
          </View>
          <Card sx={{ marginTop: 2 }}>
            <Link href={problemPdf.objectUrl} target={'_blank'}>
              <Grid gapX={1} wrap={false} sx={{ padding: 3 }}>
                <Grid.Unit size={'max'}>
                  <Text sx={{ fontSize: 1 }}>{problemPdf.key}</Text>
                </Grid.Unit>
                <Grid.Unit size={'min'}>
                  <Text sx={{ fontSize: 1 }} color={'fg.subtle'}>
                    PDF 열기
                  </Text>
                </Grid.Unit>
                <Grid.Unit size={'min'}>
                  <StyledOcticon icon={ChevronRightIcon} color={'fg.subtle'} />
                </Grid.Unit>
              </Grid>
            </Link>
          </Card>
          <View sx={{ marginTop: 5 }}>
            <Text sx={{ fontSize: 1, fontWeight: 'bold' }}>해설 PDF</Text>
          </View>
          <Card sx={{ marginTop: 2 }}>
            <Link href={solutionPdf.objectUrl} target={'_blank'}>
              <Grid gapX={1} wrap={false} sx={{ padding: 3 }}>
                <Grid.Unit size={'max'}>
                  <Text sx={{ fontSize: 1 }}>{solutionPdf.key}</Text>
                </Grid.Unit>
                <Grid.Unit size={'min'}>
                  <Text sx={{ fontSize: 1 }} color={'fg.subtle'}>
                    PDF 열기
                  </Text>
                </Grid.Unit>
                <Grid.Unit size={'min'}>
                  <StyledOcticon icon={ChevronRightIcon} color={'fg.subtle'} />
                </Grid.Unit>
              </Grid>
            </Link>
          </Card>
          <View sx={{ marginTop: 5 }}>
            <MutationFormik<BookId_bookScrapsDeleteMutation>
              mutation={graphql`
                mutation BookId_bookScrapsDeleteMutation($input: BookScrapsDeleteInput!) {
                  bookScrapsDelete(input: $input) {
                    id
                    unclassifiedBookScrapCount
                  }
                }
              `}
              initialValues={{
                book: id,
                bookScraps: [],
              }}
              config={{
                // 목록을 다시 받지 않고 지운 문제만 빼서, 불러 둔 만큼 그대로 보이게 한다.
                updater: (store) => removeBookScrapsFromList(store, id, deletingBookScrapIdsRef.current),
                onCompleted: (_, __, formikHelpers) => {
                  toast('문제가 삭제됐어요', 'success');
                  formikHelpers.resetForm();
                },
              }}
            >
              {({ submitForm, isSubmitting, values, setFieldValue, resetForm }) => {
                const isDeletable = status === 'ongoing';
                const selectedCount = values.bookScraps.length;
                return (
                  <>
                    {/* 목록을 내려도 버튼을 쓸 수 있게 헤더 아래에 붙여 둔다. */}
                    <View
                      sx={{
                        position: 'sticky',
                        top: `${headerHeight}px`,
                        zIndex: 1,
                        paddingY: 2,
                        backgroundColor: 'canvas.default',
                      }}
                    >
                      <Grid sx={{ alignItems: 'center' }} gapX={2}>
                        <Grid.Unit size={'max'}>
                          <Text sx={{ fontSize: 1, fontWeight: 'bold' }}>스크랩한 문제</Text>
                        </Grid.Unit>
                        {actions.includes('book_scraps_classification') &&
                        (selectedCount > 0 || unclassifiedBookScrapCount > 0) ? (
                          <Grid.Unit size={'min'}>
                            <DialogButton
                              variant={'outline'}
                              size={'large'}
                              renderDialog={({ isOpen, closeDialog }) => (
                                <BookBookScrapsClassificationDialog
                                  book={book}
                                  bookScraps={values.bookScraps}
                                  isOpen={isOpen}
                                  onDismiss={closeDialog}
                                  config={{
                                    onCompleted: () => {
                                      toast('일괄 재분류를 시작했어요. 끝나는 대로 반영돼요', 'success');
                                      closeDialog();
                                      resetForm();
                                    },
                                    onError: (error) => {
                                      toast(
                                        parseGraphQLError(error as GraphQLError)?.[0].message || '다시 시도해주세요',
                                        'error',
                                      );
                                    },
                                  }}
                                />
                              )}
                            >
                              {selectedCount > 0
                                ? `일괄 재분류 (선택 ${selectedCount})`
                                : `일괄 재분류 (${unclassifiedBookScrapCount})`}
                            </DialogButton>
                          </Grid.Unit>
                        ) : null}
                        {actions.includes('book_scrap_create') ? (
                          <Grid.Unit size={'min'}>
                            {/* TODO: 시중 교재 스크랩을 만들고 처음 PDF 업로드 후 스크랩을 하고 있는 중에는 disabled 상태  */}
                            <DialogButton
                              variant={'outline'}
                              renderDialog={({ isOpen, closeDialog }) => (
                                <BookBookScrapCreateDialog book={book} isOpen={isOpen} onDismiss={closeDialog} full />
                              )}
                              size={'large'}
                            >
                              스크랩하기
                            </DialogButton>
                          </Grid.Unit>
                        ) : null}
                        {isDeletable ? (
                          <Grid.Unit size={'min'}>
                            <ConfirmButton
                              size={'large'}
                              variant={'danger'}
                              onClick={() => {
                                deletingBookScrapIdsRef.current = values.bookScraps;
                                submitForm();
                              }}
                              disabled={values.bookScraps.length === 0}
                              message={'삭제된 문제는 되돌릴 수 없어요. 삭제할까요?'}
                            >
                              삭제하기
                            </ConfirmButton>
                          </Grid.Unit>
                        ) : null}
                      </Grid>
                    </View>
                    <View sx={{ marginTop: 1 }}>
                      <BookBookScrapPaginator fragmentReference={book} keepLoadedCount>
                        {({ bookScraps }, { loadMore, hasNext, isLoadingNext }) => (
                          <>
                            <Text sx={{ fontSize: 1, fontWeight: 'bold', color: 'fg.muted' }}>
                              {`총 ${numberWithCommas(bookScraps.totalCount || 0)} 문제${
                                values.bookScraps.length > 0 ? ` / ${values.bookScraps.length} 문제` : ''
                              }`}
                            </Text>
                            <View sx={{ marginTop: 2, overflowX: 'auto' }}>
                              {/* TODO: 시중 교재 스크랩을 만들고 처음 PDF 업로드 후 스크랩을 하고 있는 중에는 Loading 화면 보여줘야 함.  */}
                              <BookScrapConnectionDataTable
                                bookScrapConnection={bookScraps}
                                emptyState={
                                  <View sx={{ paddingY: 3 }}>
                                    <EmptyState title={'아직 스크랩한 문제가 없어요'} />
                                  </View>
                                }
                                renderHead={(columns) => (
                                  <View
                                    as={'thead'}
                                    sx={{
                                      borderBottomWidth: 1,
                                      borderBottomStyle: 'solid',
                                      borderBottomColor: 'border.default',
                                      backgroundColor: 'canvas.subtle',
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <View as={'tr'}>
                                      {isDeletable ? (
                                        <View
                                          as={'th'}
                                          sx={{ padding: 2, width: 40 }}
                                          onClick={(e) => {
                                            if (e.currentTarget === e.target)
                                              (
                                                e.currentTarget.querySelector('input[type=checkbox]') as HTMLElement
                                              )?.click();
                                          }}
                                        >
                                          <Checkbox
                                            disabled={isSubmitting || bookScraps.edges.length === 0}
                                            checked={
                                              values.bookScraps.length !== 0 &&
                                              values.bookScraps.length === bookScraps.edges.length
                                            }
                                            onChange={(e) => {
                                              setFieldValue(
                                                'bookScraps',
                                                e.target.checked ? bookScraps.edges.map(({ node }) => node.id) : [],
                                              );
                                            }}
                                          />
                                        </View>
                                      ) : null}
                                      {columns.map(({ field, title, width }) => (
                                        <View
                                          key={field}
                                          as={'th'}
                                          sx={{
                                            borderWidth: 1,
                                            borderStyle: 'solid',
                                            borderColor: 'border.default',
                                            minWidth: width,
                                            textAlign: 'start',
                                            padding: 2,
                                            fontWeight: 'bold',
                                            color: 'fg.muted',
                                          }}
                                        >
                                          {title}
                                        </View>
                                      ))}
                                    </View>
                                  </View>
                                )}
                                renderRowWrapper={(children, row, index) => (
                                  <RowWrapper
                                    key={index}
                                    sx={{
                                      'cursor': 'pointer',
                                      ':hover': {
                                        backgroundColor: 'canvas.inset',
                                        transition: 'background-color 250ms',
                                      },
                                    }}
                                  >
                                    {isDeletable && row.actions.includes('book_scrap_delete') ? (
                                      <View
                                        as={'td'}
                                        sx={{
                                          cursor: 'default',
                                          borderWidth: 1,
                                          borderStyle: 'solid',
                                          borderColor: 'border.default',
                                          padding: 2,
                                        }}
                                        // 칸을 눌러도 체크박스 click() 으로 넘겨서 shift 여부가 사라지므로 여기서 기억한다.
                                        onMouseDown={(e) => {
                                          isShiftClickRef.current = e.shiftKey;
                                          // shift 클릭에 표 글자가 드래그 선택되지 않게 한다.
                                          if (e.shiftKey) e.preventDefault();
                                        }}
                                        onClick={(e) => {
                                          if (e.currentTarget === e.target)
                                            (
                                              e.currentTarget.querySelector('input[type=checkbox]') as HTMLElement
                                            )?.click();
                                          e.stopPropagation();
                                        }}
                                      >
                                        <Checkbox
                                          disabled={isSubmitting}
                                          checked={values.bookScraps.includes(row.id)}
                                          onChange={(e) => {
                                            // shift 클릭이면 직전에 누른 문제부터 이 문제까지 한꺼번에 같은 상태로 바꾼다.
                                            const rows = bookScraps.edges
                                              .map(({ node }) => node)
                                              .filter((node) => !!node);
                                            const anchorIndex = isShiftClickRef.current
                                              ? rows.findIndex(({ id }) => id === lastClickedBookScrapIdRef.current)
                                              : -1;
                                            isShiftClickRef.current = false;
                                            lastClickedBookScrapIdRef.current = row.id;
                                            const targetIds =
                                              anchorIndex < 0
                                                ? [row.id]
                                                : rows
                                                    .slice(
                                                      Math.min(anchorIndex, index),
                                                      Math.max(anchorIndex, index) + 1,
                                                    )
                                                    .filter(({ actions }) => actions.includes('book_scrap_delete'))
                                                    .map(({ id }) => id);
                                            setFieldValue(
                                              'bookScraps',
                                              e.target.checked
                                                ? Array.from(new Set([...values.bookScraps, ...targetIds]))
                                                : values.bookScraps.filter((id) => !targetIds.includes(id)),
                                            );
                                          }}
                                        />
                                      </View>
                                    ) : null}
                                    <DialogHandler
                                      renderDialog={({ isOpen, closeDialog }) => {
                                        return (
                                          <BookBookScrapDeleteDialog
                                            book={book}
                                            isOpen={isOpen}
                                            onDismiss={closeDialog}
                                            initialValues={{ id: row.id }}
                                            full
                                            config={{
                                              onCompleted: () => {
                                                toast('스크랩한 문제가 삭제됐어요', 'success');
                                                resetForm();
                                              },
                                            }}
                                          />
                                        );
                                      }}
                                    >
                                      {children}
                                    </DialogHandler>
                                  </RowWrapper>
                                )}
                              />
                              {hasNext ? (
                                <Button
                                  sx={{ width: '100%', marginTop: 2 }}
                                  disabled={isLoadingNext}
                                  onClick={() => loadMore(100)}
                                >
                                  더보기
                                </Button>
                              ) : null}
                            </View>
                          </>
                        )}
                      </BookBookScrapPaginator>
                    </View>
                  </>
                );
              }}
            </MutationFormik>
          </View>
        </Grid.Unit>
        <Grid.Unit size={[1, 1, 1 / 4]}>
          <BookDescriptionList
            book={book}
            type={'activity'}
            titleUnitSize={'max'}
            descriptionUnitSize={'min'}
            renderTitle={(title) => (
              <Text fontWeight={'bold'} fontSize={1}>
                {title}
              </Text>
            )}
          />
        </Grid.Unit>
      </Grid>
    </View>
  );
};

BookId.getLayout = (page) => <HeaderSidebarNavPageLayout>{page}</HeaderSidebarNavPageLayout>;
BookId.authenticated = true;
BookId.routes = [
  {
    id: 'bookId',
    pathname: '/book/[bookId]',
    name: '시중 교재 스크랩 상세',
    permissions: ['book_read'],
  },
];

export default BookId;
